import React, {useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import clsx from "clsx";
import PropTypes from "prop-types";
import useIsMobile from '../../../hooks/useIsMobile';

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        padding: theme.spacing(2),
        position: 'fixed',
        top: 'auto',
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        gap: theme.spacing(),
        justifyContent: 'center',
        backgroundColor: 'white',
        borderTop: '1px solid #dadada',
        boxShadow: theme.shadows[8],
        zIndex: theme.zIndex.appBar,
        paddingBottom: `calc(${theme.spacing(2)}px + env(safe-area-inset-bottom))`,
        '& > *': {
            maxWidth: 960,
            width: '100%'
        }
    },
    show: {
        animation: '$show .5s ease-in-out 1',
        animationFillMode: 'forwards'
    },
    '@keyframes show': {
        from: {
            'transform': 'translateY(70px)',
            '-webkit-transform': 'translateY(70px)'
        },
        to: {
            'transform': 'translateY(0)',
            '-webkit-transform': 'translateY(0)'
        }
    },
    hide: {
        animation: '$hide .5s ease-in-out 1',
        animationFillMode: 'forwards'
    },
    '@keyframes hide': {
        from: {
            'transform': 'translateY(0)',
            '-webkit-transform': 'translateY(0)'
        },
        to: {
            'transform': 'translateY(70px)',
            '-webkit-transform': 'translateY(70px)'
        }
    }
}));

export default function FloatingBottomBar(props) {
    const {
        children,
        hideInHeight,
        autoHide = true,
        mobileOnly = false,
        alwaysRender = false,
        ...rest
    } = props;
    const classes = useStyles();
    const isMobile = useIsMobile();
    const lastScrollYRef = useRef(0);
    const [show, setShow] = useState(true);

    const hasVerticalScrollBar = document.documentElement.scrollHeight > document.documentElement.clientHeight;

    const maxHeightCanScroll = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
    ) - window.innerHeight - 70;

    const scrollHideLimit = hideInHeight === undefined ? maxHeightCanScroll : hideInHeight;

    useEffect(() => {
        if (autoHide) {
            window.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (autoHide) {
                window.removeEventListener('scroll', handleScroll);
            }
        };
    }, [autoHide, scrollHideLimit]);

    const handleScroll = () => {
        if (window.scrollY < scrollHideLimit) {
            setShow(true);
        } else {
            setShow(false);
        }

        lastScrollYRef.current = window.scrollY;
    };

    if (mobileOnly && !isMobile) {
        return null;
    }

    return (alwaysRender || hasVerticalScrollBar) && (
        <div className={clsx(classes.root, show ? classes.show : classes.hide)} {...rest}>
            {children}
        </div>
    );
};

FloatingBottomBar.propTypes = {
    hideInHeight: PropTypes.number,
    autoHide: PropTypes.bool,
    mobileOnly: PropTypes.bool,
    alwaysRender: PropTypes.bool
};
